import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "numeric-panel__alert" }
const _hoisted_2 = {
  key: 0,
  class: "text text--h7 text--negative"
}

import VPanel from './VPanel.vue';
import VPanelRow from './VPanelRow.vue';
import VPanelLayer from './VPanelLayer.vue';
import VInfoList from './VInfoList.vue';
import VInfoItem from './VInfoItem.vue';
import VSelect from './VSelect.vue';
import VTextarea from './VTextarea.vue';
import VDatePicker from './VDatePicker.vue';
import VInlineLink from './VInlineLink.vue';
import VContentEditable from './VContentEditable.vue';
import VNumericCounterValueControl from './VNumericCounterValueControl.vue';
import VNumericCounterControl from './VNumericCounterControl.vue';
import VNumericQuarterTable from './VNumericQuarterTable.vue';
import VNumericQuarterTableColumn from './VNumericQuarterTableColumn.vue';

// SVG
import NoticeSvg from '@/assets/notice.svg';

// Other
import INumeric from '@/core/Models/INumeric';
import { PropType, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    useNumericCompletion,
    useNumericContextMenu,
    useNumericForm,
    useNumericPermissions,
} from '@/mixins/NumericApi';
import ITask from '@/core/Models/ITask';
import { useTaskLink } from '@/mixins/TaskApi';
import VUserSelectorNew from './VUserSelectorNew.vue';
import {
    useNumericConditionSelector,
    useNumericCounterSelector,
    useNumericViewModeSelector,
    useTaskSelector,
} from '@/mixins/SelectorsApi';
import TaskType from '@/core/Values/TaskType';
import INumericCounter from '@/core/Models/INumericCounter';
import VButton from './VButton.vue';
import { INumericQuarterCompletion } from '@/mixins/NumericApi';
import { useNumericCounterValueApi } from '@/mixins/NumericCounterValueApi';
import QuarterNumber from '@/core/Values/QuarterNumber';
import NumericViewMode from '@/core/Values/NumericViewMode';

interface CommitControlContext {
    quarter: QuarterNumber;
    actualValue: number;
    expectedValue: number;
    quarterCompletion: INumericQuarterCompletion;
}

interface CounterControlContext {
    counter: INumericCounter;
    units?: string;
    tagName?: string | null;
    tagValue?: string | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VNumericPanel',
  props: {
    task: { type: Object as PropType<ITask | null>, default: null },
    numeric: { type: Object as PropType<INumeric>, required: true },
},
  setup(__props) {

// Components
const { t } = useI18n();
const props = __props;
const { numeric, task } = toRefs(props);

const {
    editable,
    disabled,
    isFullfiled,
    commitAsync,
    setTaskAsync,
    setAuthorAsync,
    setAssignedAsync,
    setCounterAsync,
    setCounterDivisorAsync,
    setYearAsync,
    setNameAsync,
    setRemarkAsync,
    setCommentAsync,
    setExpectedValueAsync,
    setConditionAsync,
    setViewModeAsync,
} = useNumericForm(numeric);
const { completion } = useNumericCompletion(numeric);
const { bulkInsertAsync } = useNumericCounterValueApi();
const { canUpdatePublicFields, canUpdatePrivateFields } = useNumericPermissions(numeric);
const taskLink = useTaskLink(task);
const taskSelector = useTaskSelector(
    ref({
        whereType: [TaskType.Goal],
    }),
);
const counterSelector = useNumericCounterSelector();
const conditionSelector = useNumericConditionSelector();
const viewModeSelector = useNumericViewModeSelector();

const { migrateNumericWithConfirm } = useNumericContextMenu();

let counterControl = ref({
    visible: false,
    context: null as CounterControlContext | null,
});

let counterDivisorControl = ref({
    visible: false,
    context: null as CounterControlContext | null,
});

let numericValueControl = ref({
    visible: false,
    context: null as CommitControlContext | null,
});

watch(
    () => numeric.value,
    () => {
        counterControl.value = { visible: false, context: null };
        counterDivisorControl.value = { visible: false, context: null };
        numericValueControl.value = { visible: false, context: null };
    },
);

function showCounterControl(
    event: INumericCounter | string,
    control: typeof counterDivisorControl.value,
    units?: string,
    tagName?: string | null,
    tagValue?: string | null,
) {
    if (typeof event === 'string') {
        event = { name: event } as INumericCounter;
    }

    event = event.id
        ? event
        : {
              id: -1,
              key: '',
              name: event.name,
              units: units ?? '',
          };

    let counter = event;

    if (!tagName && !tagValue && numeric.value.assigneeId) {
        tagName = 'kpi-numerics.system.user';
        tagValue = numeric.value.assigneeId;
    }

    control.visible = true;
    control.context = {
        counter,
        units,
        tagName,
        tagValue,
    };
}

function showNumericValueControl(
    quarter: QuarterNumber,
    quarterCompletion: INumericQuarterCompletion,
    actualValue: number,
    expectedValue: number,
) {
    numericValueControl.value.visible = true;
    numericValueControl.value.context = {
        quarter,
        actualValue,
        expectedValue,
        quarterCompletion,
    };
}

function deselectCounterDivisor() {
    setCounterDivisorAsync({
        counterId: 0,
        numericTagName: '',
        numericTagValue: '',
    });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(VPanel, {
    onClose: _cache[33] || (_cache[33] = ($event: any) => (_ctx.$emit('close', $event)))
  }, {
    default: _withCtx(() => [
      (!!_unref(numeric).archivedAt)
        ? (_openBlock(), _createBlock(VPanelRow, {
            key: 0,
            center: "",
            class: "numeric-panel__row--secondary"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(NoticeSvg), { class: "numeric-panel__alert-icon" }),
              _createElementVNode("h4", _hoisted_1, _toDisplayString(_unref(t)('numeric-archived')), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(VPanelRow, { first: "" }, {
        default: _withCtx(() => [
          _createVNode(VTextarea, {
            class: "simple-textarea--h2",
            "concurrency-mode": "",
            "debounce-mode": "",
            disabled: !_unref(canUpdatePrivateFields) || _unref(disabled),
            editable: _unref(editable),
            maxlength: "512",
            "model-value": _unref(numeric).name,
            required: "",
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_unref(setNameAsync)($event)))
          }, null, 8, ["disabled", "editable", "model-value"])
        ]),
        _: 1
      }),
      (_unref(task) && _unref(taskLink))
        ? (_openBlock(), _createBlock(VPanelRow, {
            key: 1,
            padding: ""
          }, {
            default: _withCtx(() => [
              (_unref(task))
                ? (_openBlock(), _createBlock(VInlineLink, {
                    key: 0,
                    class: "prevent-close",
                    subject: _unref(t)('attached-to-goal'),
                    to: _unref(taskLink)
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(task).title ? _unref(task).title : _unref(t)('untitled')), 1)
                    ]),
                    _: 1
                  }, 8, ["subject", "to"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(VPanelRow, {
        padding: "",
        separate: ""
      }, {
        default: _withCtx(() => [
          _createVNode(VInfoList, { class: "numeric-panel__info-list" }, {
            default: _withCtx(() => [
              _createVNode(VInfoItem, {
                center: "",
                label: _unref(t)('author'),
                required: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(VUserSelectorNew, {
                    class: "v-select--primary v-select--invisible v-select--no-option-padding v-select--form-user-selector",
                    "model-value": [_unref(numeric).authorId],
                    onSelected: _cache[1] || (_cache[1] = ($event: any) => (_unref(setAuthorAsync)($event)))
                  }, null, 8, ["model-value"])
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(VInfoItem, {
                center: "",
                label: _unref(t)('assignee'),
                required: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(VUserSelectorNew, {
                    class: "v-select--primary v-select--invisible v-select--no-option-padding v-select--form-user-selector",
                    "model-value": _unref(numeric).assigneeId ? [_unref(numeric).assigneeId] : [],
                    onSelected: _cache[2] || (_cache[2] = ($event: any) => (_unref(setAssignedAsync)($event)))
                  }, null, 8, ["model-value"])
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(VInfoItem, {
                center: "",
                label: _unref(t)('goal', 1)
              }, {
                default: _withCtx(() => [
                  _createVNode(VSelect, {
                    class: "v-select--primary",
                    disabled: !_unref(canUpdatePrivateFields) || _unref(disabled),
                    filterable: false,
                    label: _unref(taskSelector).label,
                    "model-value": _unref(task),
                    options: _unref(taskSelector).options.value,
                    placeholder: _unref(t)('default-placeholder', 10),
                    onOpen: _unref(taskSelector).prefetch,
                    "onOption:selected": _cache[4] || (_cache[4] = ($event: any) => (_unref(setTaskAsync)(_unref(taskSelector).reduce($event)))),
                    onSearch: _unref(taskSelector).searchAsync
                  }, {
                    "selected-option": _withCtx(() => [
                      (_unref(task) && _unref(taskLink))
                        ? (_openBlock(), _createBlock(VInlineLink, {
                            key: 0,
                            class: "inline-link--user-selectedor-option",
                            to: _unref(taskLink),
                            onMousedown: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_unref(task).title ? _unref(task).title : _unref(t)('untitled')), 1)
                            ]),
                            _: 1
                          }, 8, ["to"]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }, 8, ["disabled", "label", "model-value", "options", "placeholder", "onOpen", "onSearch"])
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(VInfoItem, {
                center: "",
                label: _unref(t)('numeric.year'),
                required: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(VDatePicker, {
                    config: {
                            yearPicker: true,
                            autoApply: true,
                            clearable: false,
                            startDate: new Date(),
                            minDate: new Date(),
                            maxDate: new Date(new Date().getFullYear() + 5, 1),
                            textInput: {
                                format: 'yyyy',
                            },
                            disabled: !_unref(canUpdatePrivateFields) || _unref(disabled),
                        },
                    editable: _unref(editable) && !_unref(completion).current.hasCommitedQuarters,
                    "model-value": _unref(numeric).year.toString(),
                    onEditRequest: _cache[5] || (_cache[5] = ($event: any) => (_unref(migrateNumericWithConfirm)(_unref(numeric)))),
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_unref(setYearAsync)($event)))
                  }, null, 8, ["config", "editable", "model-value"])
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(VInfoItem, {
                center: "",
                gap: "",
                label: _unref(t)('numeric.dividend-name'),
                required: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(VSelect, {
                    class: "v-select--primary",
                    disabled: !_unref(canUpdatePrivateFields) || _unref(disabled),
                    filterable: false,
                    label: _unref(counterSelector).label,
                    "model-value": _unref(numeric).counter,
                    options: _unref(counterSelector).options.value,
                    placeholder: _unref(t)('default-placeholder', 1),
                    taggable: "",
                    onOpen: _unref(counterSelector).prefetch,
                    "onOption:selected": _cache[7] || (_cache[7] = ($event: any) => (showCounterControl($event, _unref(counterControl), _unref(numeric).counterDivisor?.units))),
                    onSearch: _unref(counterSelector).searchAsync
                  }, null, 8, ["disabled", "label", "model-value", "options", "placeholder", "onOpen", "onSearch"]),
                  (_unref(numeric).counter)
                    ? (_openBlock(), _createBlock(VButton, {
                        key: 0,
                        class: "button--default button--secondary",
                        onClick: _cache[8] || (_cache[8] = ($event: any) => (
                            showCounterControl(
                                _unref(numeric).counter,
                                _unref(counterControl),
                                _unref(numeric).counter?.units,
                                _unref(numeric).counterTagName,
                                _unref(numeric).counterTagValue,
                            )
                        ))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(t)('control')), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(VInfoItem, {
                center: "",
                gap: "",
                label: _unref(t)('numeric.divisor-name')
              }, {
                default: _withCtx(() => [
                  _createVNode(VSelect, {
                    class: "v-select--primary",
                    clearable: "",
                    deselectFromDropdown: "",
                    disabled: !_unref(canUpdatePrivateFields) || _unref(disabled),
                    filterable: false,
                    label: _unref(counterSelector).label,
                    "model-value": _unref(numeric).counterDivisor,
                    options: _unref(counterSelector).options.value,
                    placeholder: _unref(t)('default-placeholder', 0),
                    taggable: "",
                    onOpen: _unref(counterSelector).prefetch,
                    "onOption:deselected": deselectCounterDivisor,
                    "onOption:selected": _cache[9] || (_cache[9] = ($event: any) => (showCounterControl($event, _unref(counterDivisorControl), _unref(numeric).counter?.units))),
                    onSearch: _unref(counterSelector).searchAsync
                  }, null, 8, ["disabled", "label", "model-value", "options", "placeholder", "onOpen", "onSearch"]),
                  (_unref(numeric).counterDivisor)
                    ? (_openBlock(), _createBlock(VButton, {
                        key: 0,
                        class: "button--default button--secondary",
                        onClick: _cache[10] || (_cache[10] = ($event: any) => (
                            showCounterControl(
                                _unref(numeric).counterDivisor,
                                _unref(counterDivisorControl),
                                _unref(numeric).counter?.units,
                                _unref(numeric).counterDivisorTagName,
                                _unref(numeric).counterDivisorTagValue,
                            )
                        ))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(t)('control')), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(VInfoItem, {
                center: "",
                label: _unref(t)('numeric.condition'),
                required: ""
              }, {
                default: _withCtx(() => [
                  _createVNode(VSelect, {
                    class: "v-select--primary",
                    disabled: !_unref(canUpdatePrivateFields) || _unref(disabled),
                    filterable: false,
                    label: _unref(conditionSelector).label,
                    "model-value": _unref(numeric).condition ? _unref(conditionSelector).map!(_unref(numeric).condition) : null,
                    options: _unref(conditionSelector).options,
                    placeholder: _unref(t)('default-placeholder', 1),
                    "onOption:selected": _cache[11] || (_cache[11] = ($event: any) => (_unref(setConditionAsync)(_unref(conditionSelector).reduce!($event))))
                  }, null, 8, ["disabled", "label", "model-value", "options", "placeholder"])
                ]),
                _: 1
              }, 8, ["label"]),
              _createVNode(VInfoItem, {
                center: "",
                label: _unref(t)('numeric.view-mode')
              }, {
                default: _withCtx(() => [
                  _createVNode(VSelect, {
                    class: "v-select--primary",
                    disabled: !_unref(canUpdatePrivateFields) || _unref(disabled),
                    filterable: false,
                    label: _unref(viewModeSelector).label,
                    "model-value": _unref(numeric).viewMode ? _unref(viewModeSelector).map!(_unref(numeric).viewMode) : null,
                    options: _unref(viewModeSelector).options,
                    placeholder: _unref(t)('default-placeholder', 1),
                    "onOption:selected": _cache[12] || (_cache[12] = ($event: any) => (_unref(setViewModeAsync)(_unref(viewModeSelector).reduce!($event))))
                  }, null, 8, ["disabled", "label", "model-value", "options", "placeholder"])
                ]),
                _: 1
              }, 8, ["label"]),
              (
                        _unref(numeric).counter?.units &&
                        _unref(numeric).counterDivisor?.units &&
                        _unref(numeric).counter.units.toLowerCase() !== _unref(numeric).counterDivisor.units.toLowerCase()
                    )
                ? (_openBlock(), _createElementBlock("small", _hoisted_2, _toDisplayString(_unref(t)('numeric-counter.units-not-match-description')), 1))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      (_unref(isFullfiled))
        ? (_openBlock(), _createBlock(VPanelRow, {
            key: 2,
            padding: "",
            separate: "",
            "separate-bottom": ""
          }, {
            default: _withCtx(() => [
              _createVNode(VNumericQuarterTable, {
                "view-mode": _unref(numeric).viewMode ? _unref(numeric).viewMode : _unref(NumericViewMode).Proportion
              }, {
                default: _withCtx(({ viewMode }) => [
                  _createVNode(VNumericQuarterTableColumn, {
                    "actual-value": _unref(numeric).actualValueQ1,
                    "can-update-private-fields": _unref(canUpdatePrivateFields),
                    "can-update-public-fields": _unref(canUpdatePublicFields),
                    committed: !!_unref(numeric).committedAtQ1,
                    disabled: _unref(disabled),
                    "expected-value": _unref(numeric).expectedValueQ1,
                    quarter: 1,
                    "quarter-completion": _unref(completion).quarter1,
                    "view-mode": viewMode,
                    onAddValue: _cache[13] || (_cache[13] = ($event: any) => (
                            showNumericValueControl(
                                1,
                                _unref(completion).quarter1,
                                _unref(numeric).actualValueQ1,
                                _unref(numeric).expectedValueQ1,
                            )
                        )),
                    onCommitValue: _cache[14] || (_cache[14] = ($event: any) => (_unref(commitAsync)(1))),
                    "onUpdate:expectedValue": _cache[15] || (_cache[15] = ($event: any) => (_unref(setExpectedValueAsync)({ expectedValueQ1: $event })))
                  }, null, 8, ["actual-value", "can-update-private-fields", "can-update-public-fields", "committed", "disabled", "expected-value", "quarter-completion", "view-mode"]),
                  _createVNode(VNumericQuarterTableColumn, {
                    "actual-value": _unref(numeric).actualValueQ2,
                    "can-update-private-fields": _unref(canUpdatePrivateFields),
                    "can-update-public-fields": _unref(canUpdatePublicFields),
                    committed: !!_unref(numeric).committedAtQ2,
                    disabled: _unref(disabled),
                    "expected-value": _unref(numeric).expectedValueQ2,
                    quarter: 2,
                    "quarter-completion": _unref(completion).quarter2,
                    "view-mode": viewMode,
                    onAddValue: _cache[16] || (_cache[16] = ($event: any) => (
                            showNumericValueControl(
                                2,
                                _unref(completion).quarter2,
                                _unref(numeric).actualValueQ2,
                                _unref(numeric).expectedValueQ2,
                            )
                        )),
                    onCommitValue: _cache[17] || (_cache[17] = ($event: any) => (_unref(commitAsync)(2))),
                    "onUpdate:expectedValue": _cache[18] || (_cache[18] = ($event: any) => (_unref(setExpectedValueAsync)({ expectedValueQ2: $event })))
                  }, null, 8, ["actual-value", "can-update-private-fields", "can-update-public-fields", "committed", "disabled", "expected-value", "quarter-completion", "view-mode"]),
                  _createVNode(VNumericQuarterTableColumn, {
                    "actual-value": _unref(numeric).actualValueQ3,
                    "can-update-private-fields": _unref(canUpdatePrivateFields),
                    "can-update-public-fields": _unref(canUpdatePublicFields),
                    committed: !!_unref(numeric).committedAtQ3,
                    disabled: _unref(disabled),
                    "expected-value": _unref(numeric).expectedValueQ3,
                    quarter: 3,
                    "quarter-completion": _unref(completion).quarter3,
                    "view-mode": viewMode,
                    onAddValue: _cache[19] || (_cache[19] = ($event: any) => (
                            showNumericValueControl(
                                3,
                                _unref(completion).quarter3,
                                _unref(numeric).actualValueQ3,
                                _unref(numeric).expectedValueQ3,
                            )
                        )),
                    onCommitValue: _cache[20] || (_cache[20] = ($event: any) => (_unref(commitAsync)(3))),
                    "onUpdate:expectedValue": _cache[21] || (_cache[21] = ($event: any) => (_unref(setExpectedValueAsync)({ expectedValueQ3: $event })))
                  }, null, 8, ["actual-value", "can-update-private-fields", "can-update-public-fields", "committed", "disabled", "expected-value", "quarter-completion", "view-mode"]),
                  _createVNode(VNumericQuarterTableColumn, {
                    "actual-value": _unref(numeric).actualValueQ4,
                    "can-update-private-fields": _unref(canUpdatePrivateFields),
                    "can-update-public-fields": _unref(canUpdatePublicFields),
                    committed: !!_unref(numeric).committedAtQ4,
                    disabled: _unref(disabled),
                    "expected-value": _unref(numeric).expectedValueQ4,
                    quarter: 4,
                    "quarter-completion": _unref(completion).quarter4,
                    "view-mode": viewMode,
                    onAddValue: _cache[22] || (_cache[22] = ($event: any) => (
                            showNumericValueControl(
                                4,
                                _unref(completion).quarter4,
                                _unref(numeric).actualValueQ4,
                                _unref(numeric).expectedValueQ4,
                            )
                        )),
                    onCommitValue: _cache[23] || (_cache[23] = ($event: any) => (_unref(commitAsync)(4))),
                    "onUpdate:expectedValue": _cache[24] || (_cache[24] = ($event: any) => (_unref(setExpectedValueAsync)({ expectedValueQ4: $event })))
                  }, null, 8, ["actual-value", "can-update-private-fields", "can-update-public-fields", "committed", "disabled", "expected-value", "quarter-completion", "view-mode"])
                ]),
                _: 1
              }, 8, ["view-mode"])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(VPanelRow, {
        label: _unref(t)('remark'),
        "label-separate": "",
        padding: "",
        separate: ""
      }, {
        default: _withCtx(() => [
          _createVNode(VContentEditable, {
            class: "simple-contenteditable--great simple-contenteditable--unlimited inline-100-x inline-fluid",
            "concurrency-mode": "",
            "debounce-mode": "",
            disabled: !_unref(canUpdatePrivateFields) || _unref(disabled),
            editable: _unref(editable),
            maxlength: "4096",
            "model-value": _unref(numeric).remark,
            "onUpdate:modelValue": _cache[25] || (_cache[25] = ($event: any) => (_unref(setRemarkAsync)($event)))
          }, null, 8, ["disabled", "editable", "model-value"])
        ]),
        _: 1
      }, 8, ["label"]),
      _createVNode(VPanelRow, {
        label: _unref(t)('comment'),
        "label-separate": "",
        padding: "",
        separate: ""
      }, {
        default: _withCtx(() => [
          _createVNode(VContentEditable, {
            class: "simple-contenteditable--great simple-contenteditable--unlimited inline-100-x inline-fluid",
            "concurrency-mode": "",
            "debounce-mode": "",
            disabled: !_unref(canUpdatePublicFields) || _unref(disabled),
            editable: _unref(editable),
            maxlength: "4096",
            "model-value": _unref(numeric).comment,
            "onUpdate:modelValue": _cache[26] || (_cache[26] = ($event: any) => (_unref(setCommentAsync)($event)))
          }, null, 8, ["disabled", "editable", "model-value"])
        ]),
        _: 1
      }, 8, ["label"]),
      _createVNode(VPanelLayer, {
        visible: _unref(counterControl).visible,
        "onUpdate:visible": [
          _cache[27] || (_cache[27] = ($event: any) => ((_unref(counterControl).visible) = $event)),
          _cache[28] || (_cache[28] = ($event: any) => (_unref(counterControl).context = null))
        ],
        header: ""
      }, {
        default: _withCtx(({ close }) => [
          (_unref(counterControl).context)
            ? (_openBlock(), _createBlock(VNumericCounterControl, {
                key: 0,
                counter: _unref(counterControl).context.counter,
                "tag-name": _unref(counterControl).context.tagName,
                "tag-value": _unref(counterControl).context.tagValue,
                units: _unref(counterControl).context.units,
                onClose: close,
                onSubmit: _unref(setCounterAsync)
              }, null, 8, ["counter", "tag-name", "tag-value", "units", "onClose", "onSubmit"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["visible"]),
      _createVNode(VPanelLayer, {
        visible: _unref(counterDivisorControl).visible,
        "onUpdate:visible": [
          _cache[29] || (_cache[29] = ($event: any) => ((_unref(counterDivisorControl).visible) = $event)),
          _cache[30] || (_cache[30] = ($event: any) => (_unref(counterDivisorControl).context = null))
        ],
        header: ""
      }, {
        default: _withCtx(({ close }) => [
          (_unref(counterDivisorControl).context)
            ? (_openBlock(), _createBlock(VNumericCounterControl, {
                key: 0,
                counter: _unref(counterDivisorControl).context.counter,
                "tag-name": _unref(counterDivisorControl).context.tagName,
                "tag-value": _unref(counterDivisorControl).context.tagValue,
                units: _unref(counterDivisorControl).context.units,
                onClose: close,
                onSubmit: _unref(setCounterDivisorAsync)
              }, null, 8, ["counter", "tag-name", "tag-value", "units", "onClose", "onSubmit"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["visible"]),
      (_unref(numericValueControl).context)
        ? (_openBlock(), _createBlock(VPanelLayer, {
            key: 3,
            visible: _unref(numericValueControl).visible,
            "onUpdate:visible": [
              _cache[31] || (_cache[31] = ($event: any) => ((_unref(numericValueControl).visible) = $event)),
              _cache[32] || (_cache[32] = ($event: any) => (_unref(numericValueControl).context = null))
            ],
            header: ""
          }, {
            default: _withCtx(({ close }) => [
              (_unref(numericValueControl).context && _unref(numeric).counter)
                ? (_openBlock(), _createBlock(VNumericCounterValueControl, {
                    key: 0,
                    "actual-value": _unref(numericValueControl).context.actualValue,
                    counter: _unref(numeric).counter,
                    "counter-divisor": _unref(numeric).counterDivisor,
                    "expected-value": _unref(numericValueControl).context.expectedValue,
                    numeric: _unref(numeric),
                    quarter: _unref(numericValueControl).context.quarter,
                    "quarter-completion": _unref(numericValueControl).context.quarterCompletion,
                    onClose: close,
                    onSubmit: _unref(bulkInsertAsync)
                  }, null, 8, ["actual-value", "counter", "counter-divisor", "expected-value", "numeric", "quarter", "quarter-completion", "onClose", "onSubmit"]))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["visible"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})